// src/components/common/taskbox/UpcomingActivitiesWidget.js
import React from 'react';
import { Calendar, Clock } from 'lucide-react';

const UpcomingActivitiesWidget = ({ activities = [], theme }) => {
    // Activities now contains upcoming tasks with deadline/due_date
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'immediate': return theme === 'dark' ? 'text-red-400' : 'text-red-600';
            case 'urgent': return theme === 'dark' ? 'text-orange-400' : 'text-orange-600';
            case 'next_in_line': return theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600';
            case 'scheduled': return theme === 'dark' ? 'text-green-400' : 'text-green-600';
            case 'deferred': return theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
            default: return theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
        }
    };

    const formatDeadline = (dateString, daysUntil) => {
        if (!dateString) return '';

        if (daysUntil === 0) return 'Today';
        if (daysUntil === 1) return 'Tomorrow';
        if (daysUntil < 0) return `${Math.abs(daysUntil)}d overdue`;
        if (daysUntil < 7) return `In ${daysUntil} days`;

        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata' });
    };

    // Sort by days_until_deadline (already sorted by backend, but ensure)
    const sortedTasks = [...activities];

    return (
        <div className={`rounded-xl p-6 border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
            <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    Upcoming Tasks
                </h3>
                <span className={`text-sm px-3 py-1 rounded-full ${
                    theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                }`}>
                    {sortedTasks.length}
                </span>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
                {sortedTasks.length === 0 ? (
                    <div className="text-center py-8">
                        <Calendar className={`w-12 h-12 mx-auto mb-2 ${
                            theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                        }`} />
                        <p className={`text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            No upcoming tasks
                        </p>
                    </div>
                ) : (
                    sortedTasks.map((task, index) => {
                        const deadlineDate = task.deadline || task.due_date;
                        const daysUntil = task.days_until_deadline !== undefined
                            ? task.days_until_deadline
                            : Math.floor((new Date(deadlineDate) - new Date()) / (1000 * 60 * 60 * 24));

                        return (
                            <div
                                key={task.id || index}
                                className={`p-3 rounded-lg border ${
                                    theme === 'dark'
                                        ? 'bg-gray-700/50 border-gray-600'
                                        : 'bg-gray-50 border-gray-200'
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <h4 className={`text-sm font-medium ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        {task.title}
                                    </h4>
                                    <Clock className={`w-4 h-4 flex-shrink-0 ${getPriorityColor(task.priority)}`} />
                                </div>

                                {task.description && (
                                    <p className={`text-xs mb-2 ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                        {task.description}
                                    </p>
                                )}

                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                            daysUntil === 0
                                                ? theme === 'dark' ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-600'
                                                : daysUntil === 1
                                                ? theme === 'dark' ? 'bg-orange-900/50 text-orange-400' : 'bg-orange-100 text-orange-600'
                                                : theme === 'dark' ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-600'
                                        }`}>
                                            📅 {formatDeadline(deadlineDate, daysUntil)}
                                        </span>
                                        {task.task_type && (
                                            <span className={`text-xs px-2 py-0.5 rounded ${
                                                theme === 'dark' ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                                            }`}>
                                                {task.task_type}
                                            </span>
                                        )}
                                    </div>
                                    <span className={`text-xs ${getPriorityColor(task.priority)}`}>
                                        {task.priority?.replace('_', ' ')}
                                    </span>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default UpcomingActivitiesWidget;
